import { useEffect, useRef, useCallback } from 'react';
import type { DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/driver-tour.css';
import { Box, Button, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExploreIcon from '@mui/icons-material/Explore';
import {
  createResortDriver,
  filterAvailableTourSteps,
  hasSeenTourHint,
  markTourHintSeen,
} from '../utils/tourUtils';

const ACCENT = '#2C3E2D';

interface UseDriverTourOptions {
  steps: DriveStep[];
  tourKey: string;
  onComplete?: () => void;
}

export function useDriverTour({ steps, tourKey, onComplete }: UseDriverTourOptions) {
  const driverRef = useRef<ReturnType<typeof createResortDriver> | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    driverRef.current = createResortDriver(steps, () => onCompleteRef.current?.());
    return () => {
      driverRef.current?.destroy();
    };
  }, [steps, tourKey]);

  const startTour = useCallback(() => {
    const available = filterAvailableTourSteps(steps);
    if (available.length === 0) return;

    markTourHintSeen(tourKey);
    driverRef.current?.setSteps(available);
    driverRef.current?.drive();
  }, [steps, tourKey]);

  const hasSteps = steps.length > 0;
  const showHint = hasSteps && !hasSeenTourHint(tourKey);

  return { startTour, tourKey, showHint, hasSteps };
}

interface TourButtonProps {
  onStart: () => void;
  label?: string;
  tourKey?: string;
  showPulse?: boolean;
}

export function TourButton({
  onStart,
  label = 'Need Help?',
  tourKey,
  showPulse = false,
}: TourButtonProps) {
  const isTourLabel = label.toLowerCase().includes('tour');
  const pulse = showPulse && tourKey && !hasSeenTourHint(tourKey);

  const handleClick = () => {
    if (tourKey) markTourHintSeen(tourKey);
    onStart();
  };

  return (
    <Box
      className={pulse ? 'resort-tour-button-pulse' : undefined}
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        zIndex: 1300,
      }}
    >
      <Button
        onClick={handleClick}
        startIcon={isTourLabel ? <ExploreIcon /> : <HelpOutlineIcon />}
        aria-label={label}
        sx={{
          bgcolor: ACCENT,
          color: '#FAFAF8',
          borderRadius: 0,
          px: 2.5,
          py: 1.25,

          fontSize: { xs: '0.75rem', sm: '0.8rem' },
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          border: '1px solid',
          borderColor: 'rgba(250,250,248,0.12)',
          '&:hover': { bgcolor: '#1B2A1C', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
        }}
      >
        {label}
      </Button>
      {pulse && (
        <Typography
          sx={{
            position: 'absolute',
            top: -20,
            right: 0,
            fontSize: '0.62rem',
            color: ACCENT,

            whiteSpace: 'nowrap',
            bgcolor: '#FAFAF8',
            px: 0.75,
            py: 0.25,
            border: '1px solid #E2DDD8',
          }}
        >
          New here?
        </Typography>
      )}
    </Box>
  );
}
